<template>
  <!-- RQ "System Scanner" (Android FragmentSystemScanner): manufacturer / serial / model per unit of each system.
       Serial / model: type it, or scan with the camera (barcode / QR, same scanners as Unit Detail). -->
  <div>
    <div ref="rqScannerModal" class="modal fade" tabindex="-1" aria-hidden="true">
      <div
        class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
        <div class="modal-content">
          <div class="modal-body !p-3 !pb-0 font-roboto flex flex-col">
            <FxModalHeader title="System Scanner" />

            <!-- Android layout: "Add New System" bar, then per system a grey header (type · Add Item / Remove System)
                 and its units (title · Remove, Manufacturer, Serial / Model with barcode + QR scan buttons, divider) -->
            <div class="flex-1 flex flex-col px-[6px] pt-[12px] pb-[12px]">
              <!-- Add New System / Add Item: a dropdown (Android bottom sheet); the clear layer behind closes it -->
              <div v-if="menu !== null" class="fixed inset-0 z-[4]" @click="menu = null" />
              <div v-if="editable" class="relative">
                <button type="button"
                  class="flex items-center justify-between w-full px-[12px] py-[8px] rounded-[8px] border-0 bg-[#8479E1] hover:bg-[#7468D6] text-[#fff] text-[15px] font-medium"
                  @click="openMenu('system')">
                  Add New System
                  <span class="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#fff] text-[#8479E1] text-[20px] leading-none">+</span>
                </button>
                <div v-if="menu === 'system'" class="absolute right-0 top-full mt-[4px] z-[5] min-w-[170px] p-[4px] rounded-[8px] border border-[#E1E5EF] bg-[#fff] shadow-[0px_4px_12px_0px_#0000001A]">
                  <button v-for="o in systemTypeOptions" :key="o.value" type="button" class="block w-full text-left px-[12px] py-[9px] rounded-[6px] border-0 bg-[#fff] hover:bg-[#F3F4F7] text-[14px] text-[#333333]" @click="addSystem(o.value)">
                    {{ o.label }}
                  </button>
                </div>
              </div>

              <div v-for="(sys, si) in systems" :key="si" class="mt-[12px]">
                <div class="flex items-center justify-between gap-[10px] px-[10px] py-[10px] rounded-[6px] bg-[#EFEFEF]">
                  <span class="text-[17px] font-bold text-[#333333]">{{ capitalize(sys.system_type) }} System</span>
                  <div v-if="editable" class="flex flex-col items-end gap-[5px]">
                    <div class="relative">
                      <button type="button" class="border-0 bg-transparent p-0 text-[14px] font-medium text-[#7695FF] hover:underline"
                        @click="openMenu(si)">
                        Add Item
                      </button>
                      <div v-if="menu === si" class="absolute right-0 top-full mt-[4px] z-[5] min-w-[170px] p-[4px] rounded-[8px] border border-[#E1E5EF] bg-[#fff] shadow-[0px_4px_12px_0px_#0000001A]">
                        <button v-for="u in unitOptions(sys)" :key="u.value" type="button" class="block w-full text-left px-[12px] py-[9px] rounded-[6px] border-0 bg-[#fff] hover:bg-[#F3F4F7] text-[14px] text-[#333333]" @click="addItem(sys, u.value)">
                          {{ u.label }}
                        </button>
                      </div>
                    </div>
                    <button type="button" class="border-0 bg-transparent p-0 text-[14px] font-medium text-[#EE5851] hover:underline"
                      @click="removeSystem(si)">
                      Remove System
                    </button>
                  </div>
                </div>
                <div v-for="(item, ii) in sys.system_items" :key="ii" class="flex flex-col px-[10px] pt-[10px]">
                  <div class="flex items-center justify-between gap-[10px]">
                    <span class="text-[15px] font-medium text-[#333333]">{{ item.unit }}</span>
                    <button v-if="editable" type="button" class="border-0 bg-transparent p-0 text-[13px] text-[#EE5851] hover:underline"
                      @click="sys.system_items.splice(ii, 1)">
                      Remove
                    </button>
                  </div>
                  <span class="uqcLabel mt-[5px]">Manufacturer</span>
                  <FxSelect v-model="item.manufacturer" :options="manufacturers" label-key="title" value-key="value"
                    placeholder="Select Manufacturer" :disabled="!editable" />
                  <template v-for="f in fields" :key="f.key">
                    <span class="uqcLabel mt-[10px]">{{ f.label }}</span>
                    <div class="flex items-center gap-[5px]">
                      <div class="flex-1 min-w-0">
                        <inputComponent v-model="item[f.key]" :placeholder="`Type or Scan ${f.label.toLowerCase()}`"
                          :disabled="!editable" :custom-class="fieldClass" />
                      </div>
                      <template v-if="editable">
                        <button v-for="t in scanTypes" :key="t.type" type="button" :title="t.title"
                          class="flex-shrink-0 flex items-center justify-center w-[42px] h-[42px] p-0 border-0 bg-transparent hover:opacity-[0.8]"
                          @click="openScan(item, f.key, t.type)">
                          <img :src="t.icon" :alt="t.title" class="w-[42px] h-[42px] object-contain">
                        </button>
                      </template>
                    </div>
                  </template>
                  <div class="h-[1px] bg-[#E1E5EF] mx-[10px] mt-[20px]" />
                </div>
              </div>

              <p v-if="!systems.length" class="m-0 text-[14px] text-[#999999] text-center py-[20px]">No systems added</p>
            </div>

            <div class="uqcFooter flex items-center justify-end gap-[10px]">
              <button type="button" class="uqcBtnCancel" data-bs-dismiss="modal">{{ editable ? 'Cancel' : 'Close' }}</button>
              <button v-if="editable" type="button" class="uqcBtnNext" @click="save">Update</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- camera scanner over the System Scanner (Unit Detail's Scanner / ModalScanner): first scan fills the field -->
    <div ref="scanModal" class="modal fade fxModal rqScanCamModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 500px">
        <div class="modal-content">
          <div class="modal-body p-0">
            <Scanner v-if="scan.type === 'barcode'" :show-modal="scan.open" @close="closeScan" @scan-result="onScan" />
            <ModalScanner v-else :show-modal="scan.open" @close="closeScan" @scan-result="onScan" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import FxSelect from "../partialComponent/FxSelect.vue";
import inputComponent from "../partialComponent/inputComponent.vue";
import { rqApi } from "@/services/rqApi";
import Scanner from "../nest_components/Scanner.vue";
import ModalScanner from "../nest_components/ModalScanner.vue";
import barcodeIcon from "../../assets/images/barcode-icon.png";
import qrIcon from "../../assets/images/qrcode-icon.png";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";

const toast = useToast();
const FIRST_UNIT = { package: "Package Unit", split: "Outdoor Unit" };
const UNITS = { package: ["Package Unit", "Thermostat"], split: ["Outdoor Unit", "Indoor Unit", "Thermostat"] };
const blank = (s) => !String(s ?? "").trim();
const fail = (msg) => { toast.error(msg); return false; };

export default {
  components: { FxModalHeader, FxSelect, inputComponent, Scanner, ModalScanner },
  props: {
    orderId: { type: [Number, String], required: true },
    // Android: editing only while order.status < 9
    editable: { type: Boolean, default: false },
    // order.type: the first system offered when nothing is saved yet (Android: split → Split System, else Package)
    orderType: { type: String, default: "" },
  },
  emits: ["changed"],
  data() {
    return {
      systems: [],
      manufacturers: [],
      systemTypeOptions: [{ label: "Package System", value: "package" }, { label: "Split System", value: "split" }],
      fields: [{ key: "serial", label: "Serial Number" }, { key: "model", label: "Model Number" }],
      scanTypes: [{ type: "barcode", title: "Scan barcode", icon: barcodeIcon }, { type: "qrcode", title: "Scan QR code", icon: qrIcon }],
      scan: { open: false, type: "barcode", item: null, key: "" },
      // open dropdown: "system" (Add New System), a system index (its Add Item) or null
      menu: null,
      fieldClass: "!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333] disabled:!bg-[#F7F8FA] disabled:cursor-not-allowed",
    };
  },
  mounted() {
    this.bsModal = new Modal(this.$refs.rqScannerModal, { backdrop: "static", keyboard: false });
    this.$refs.rqScannerModal.addEventListener("hidden.bs.modal", enableScroll);
    this.scanModal = new Modal(this.$refs.scanModal, { backdrop: false, keyboard: false });
    // the scanner's ✕ / Keep Editing dismiss the camera modal: stop the camera with it
    this.$refs.scanModal.addEventListener("hidden.bs.modal", this.stopScan);
  },
  beforeUnmount() {
    this.$refs.rqScannerModal?.removeEventListener("hidden.bs.modal", enableScroll);
    this.bsModal?.dispose();
    this.$refs.scanModal?.removeEventListener("hidden.bs.modal", this.stopScan);
    this.scanModal?.dispose();
  },
  methods: {
    async open() {
      const [list, mf] = await Promise.all([
        rqApi.barcodes(this.orderId),
        this.manufacturers.length ? null : rqApi.barcodeManufacturers(),
      ]);
      if (!list.ok) return toast.error(list.message || "Unable to load systems.");
      if (mf?.ok && Array.isArray(mf.body)) this.manufacturers = mf.body;
      const ids = this.manufacturers.map(m => String(m.value));
      // empty list comes back as {}: start with one system of the order's type (Android)
      this.systems = Array.isArray(list.body) && list.body.length
        ? list.body.map(s => ({
          system_type: String(s.system_type || "").toLowerCase(),
          system_items: (s.system_items || []).map(i => ({
            serial: i.serial || "",
            model: i.model || "",
            unit: i.unit,
            // a manufacturer that is not in the picker any more is cleared so validation asks again (Android)
            manufacturer: i.manufacturer != null && ids.includes(String(i.manufacturer)) ? i.manufacturer : null,
          })),
        }))
        : (this.editable ? [this.newSystem((this.orderType || "").toLowerCase() === "split" ? "split" : "package")] : []);
      this.menu = null;
      disableScroll();
      this.bsModal.show();
    },
    openScan(item, key, type) {
      this.scan = { open: true, type, item, key };
      this.scanModal.show();
    },
    stopScan() {
      this.scan.open = false;
    },
    closeScan() {
      this.scanModal.hide();
    },
    onScan(text) {
      const value = String(text || "").trim();
      this.closeScan();
      if (!value) return;
      // Android isUniqueSerial: a scanned serial already used on another unit is refused
      const taken = this.scan.key === "serial" && this.systems.some(s => s.system_items.some(i => i !== this.scan.item && i.serial.trim() === value));
      if (taken) return toast.error("This serial number already exist, serial number should be unique");
      this.scan.item[this.scan.key] = value;
    },
    capitalize(s) {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
    },
    newSystem(type) {
      return { system_type: type, system_items: [{ serial: "", model: "", unit: FIRST_UNIT[type], manufacturer: null }] };
    },
    unitOptions(sys) {
      return (UNITS[sys.system_type] || UNITS.package).map(u => ({ label: u, value: u }));
    },
    // Android: validate what is there before the system-type / item picker opens
    openMenu(key) {
      this.menu = this.menu !== key && this.isValid(true) ? key : null;
    },
    addSystem(type) {
      this.menu = null;
      this.systems.push(this.newSystem(type));
    },
    addItem(sys, unit) {
      this.menu = null;
      sys.system_items.push({ serial: "", model: "", unit, manufacturer: null });
    },
    removeSystem(index) {
      this.$dialog.show({
        title: "Alert",
        text: "Are you sure you want to remove this system? It will also remove its items",
        buttonText: "Yes",
        onConfirm: () => this.systems.splice(index, 1),
      });
    },
    // forAdd: only check the items already there before adding a system / item (Android isValid)
    isValid(forAdd) {
      if (!this.systems.length && !forAdd) return fail("Add at least one item");
      const serials = new Set();
      for (const sys of this.systems) {
        if (!sys.system_items.length) {
          if (!forAdd) return fail("Add at least one item");
          continue;
        }
        for (const item of sys.system_items) {
          if (item.manufacturer == null || item.manufacturer === "") return fail("Please select manufacturer.");
          if (blank(item.serial) || blank(item.model)) return fail("All fields are required.");
          const serial = item.serial.trim();
          if (serials.has(serial)) return fail("This serial number already exist, serial number should be unique");
          serials.add(serial);
        }
      }
      return true;
    },
    async save() {
      if (!this.isValid(false)) return;
      const barcode = this.systems.map(s => ({
        system_type: s.system_type,
        system_items: s.system_items.map(i => ({
          serial: i.serial.trim(), model: i.model.trim(), unit: i.unit, manufacturer: i.manufacturer,
        })),
      }));
      const res = await rqApi.saveBarcodes(this.orderId, barcode);
      if (!res.ok) return toast.error(res.message || "Unable to update the systems.");
      toast.success("Updated Successfully");
      this.$emit("changed");
      this.bsModal.hide();
    },
  },
};
</script>

<style>
/* camera modal sits over the System Scanner modal, with its own dim background */
.rqScanCamModal { z-index: 1070; background: rgba(0, 0, 0, 0.35); }
</style>
